import React from 'react';
import { Link } from 'react-router-dom';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="w-full py-4 px-6 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/bloodResults.png" alt="Blood Results Logo" className="h-8 w-auto" />
            <span className="text-lg font-semibold text-gray-900">Blood Results</span>
          </Link>
        </div>
      </header>

      {/* Brand Banner */}
      <div className="w-full py-6 bg-white flex justify-center border-t border-gray-200">
        <img
          src="/blood-results-brand.svg"
          alt="Blood Results Brand"
          className="h-20 w-auto"
        />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
          <p className="text-sm text-gray-500 mb-8">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using Blood Results ("the Service"), operated by KNX Associates Ltd ("we", "us", or "our"),
                you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may
                not access the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                Blood Results is a platform that:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li>Parses blood test results from PDF documents using intelligent parsing technology</li>
                <li>Stores and organises your health data securely</li>
                <li>Visualises trends in your blood test results over time</li>
                <li>Enables export of data to third-party services.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Medical Disclaimer</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-gray-800 font-semibold mb-2">IMPORTANT MEDICAL DISCLAIMER</p>
                <p className="text-gray-700 leading-relaxed">
                  Blood Results is NOT a medical service and does NOT provide medical advice, diagnosis, or treatment.
                  The Service is designed solely for personal health data organization and visualization purposes.
                </p>
              </div>
              <ul className="list-disc ml-6 text-gray-700 space-y-2">
                <li>Always consult with qualified healthcare professionals regarding your health and medical conditions</li>
                <li>Do not disregard professional medical advice or delay seeking it based on information from this Service</li>
                <li>In case of a medical emergency, contact emergency services immediately</li>
                <li>We do not validate, verify, or guarantee the accuracy of parsed data</li>
                <li>You are responsible for verifying all data against your original laboratory reports</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Accounts</h2>

              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4.1 Account Creation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To use the Service, you must create an account. You agree to provide accurate, current, and complete
                    information during registration and to update such information to keep it accurate and complete.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4.2 Account Security</h3>
                  <p className="text-gray-700 leading-relaxed mb-2">You are responsible for:</p>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1">
                    <li>Maintaining the confidentiality of your account credentials</li>
                    <li>All activities that occur under your account</li>
                    <li>Notifying us immediately of any unauthorized access</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4.3 Account Termination</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We reserve the right to suspend or terminate your account if you violate these Terms or engage in
                    fraudulent, illegal, or harmful activities.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. User Obligations</h2>
              <p className="text-gray-700 leading-relaxed mb-2">You agree NOT to:</p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li>Upload documents that do not belong to you without proper authorization</li>
                <li>Share your account credentials with third parties</li>
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems or networks</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Upload malicious code, viruses, or harmful software</li>
                <li>Interfere with or disrupt the Service or servers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Accuracy and Responsibility</h2>
              <ul className="list-disc ml-6 text-gray-700 space-y-2">
                <li>Our intelligent parsing technology strives for accuracy but is not infallible</li>
                <li>You must verify all parsed data against your original laboratory reports</li>
                <li>We are not responsible for errors in parsing or interpretation of your documents</li>
                <li>You retain full responsibility for decisions made based on data in the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Intellectual Property Rights</h2>

              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">7.1 Our Rights</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The Service, including all software, designs, graphics, and content (excluding your data), is owned
                    by KNX Associates Ltd and protected by copyright, trademark, and other intellectual property laws.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">7.2 Your Rights</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You retain all rights to your health data. By using the Service, you grant us a limited license to
                    process, store, and display your data solely for the purpose of providing the Service to you.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service may integrate with third-party services (e.g., Apple Health). Your use of these services is
                subject to their respective terms and conditions. We are not responsible for the actions, policies, or
                content of third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Fees and Payment</h2>
              <p className="text-gray-700 leading-relaxed">
                Some features of the Service may require payment. We reserve the right to introduce fees or modify
                pricing at any time with reasonable notice. All fees are non-refundable unless otherwise stated.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <div className="bg-gray-100 p-4 rounded border border-gray-300">
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>TO THE FULLEST EXTENT PERMITTED BY LAW:</strong>
                </p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li>The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind</li>
                  <li>We do not warrant that the Service will be uninterrupted, error-free, or secure</li>
                  <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Our total liability to you for any claims shall not exceed the amount you paid us (if any) in the past 12 months</li>
                  <li>We are not liable for any errors in data parsing or interpretation</li>
                  <li>We are not responsible for any health decisions made based on data in the Service</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold harmless KNX Associates Ltd, its officers, directors, employees, and
                agents from any claims, damages, liabilities, and expenses (including legal fees) arising from your use
                of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Data Retention and Deletion</h2>
              <p className="text-gray-700 leading-relaxed">
                You may request deletion of your account and data at any time. Upon deletion, we will permanently remove
                your data within 30 days, except where retention is required by law. Backups may persist for up to 90 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of material changes via
                email or through the Service. Continued use of the Service after changes constitutes acceptance of the
                modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms are governed by the laws of England and Wales. Any disputes shall be resolved through the
                courts of England and Wales.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Before initiating legal proceedings, you agree to attempt to resolve disputes through good-faith
                negotiation by contacting us at: legal@knx-associates.co.uk
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited
                or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms, together with our Privacy Policy and Cookie Policy, constitute the entire agreement between
                you and KNX Associates Ltd regarding the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <ul className="space-y-1 text-gray-700">
                <li><strong>Company:</strong> KNX Associates Ltd</li>
                <li><strong>Trading as:</strong> Blood Results</li>
                <li><strong>Email:</strong> <a href="mailto:legal@knx-associates.co.uk" className="text-blue-600 hover:underline">legal@knx-associates.co.uk</a></li>
                <li><strong>Support:</strong> <a href="mailto:support@knx-associates.co.uk" className="text-blue-600 hover:underline">support@knx-associates.co.uk</a></li>
              </ul>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <Link to="/" className="text-blue-600 hover:text-blue-700">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full py-6 px-6 bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; 2025 Blood-Results.co.uk A KNX-Associates Ltd Brand. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
